import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import MarketCard from '../components/MarketCard';
import StatsCard from '../components/StatsCard';
import BalanceGraph from '../components/BalanceGraph';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, TrendingUp, Wallet, Target, Trophy, ArrowRight, XCircle, AlertCircle } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { cn } from '../lib/utils';

const API = `/api`;

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [markets, setMarkets] = useState([]);
  const [bets, setBets] = useState([]);
  const [betStats, setBetStats] = useState({ total_profit: 0, total_loss: 0, total_pending: 0 });
  const [transactions, setTransactions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bottomLeaderboard, setBottomLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toPayCount, setToPayCount] = useState(0);
  const [toConfirmCount, setToConfirmCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Parallelize non-conflicting requests to speed up loading
      // Since we enabled WAL mode, concurrent reads are now efficient
      const [marketsRes, betsRes, leaderboardRes, bottomLeaderboardRes, transactionsRes, settlementsRes] = await Promise.all([
        apiClient.get(`/markets?status=open&limit=6`),
        apiClient.get(`/bets?limit=20`),
        apiClient.get(`/stats/leaderboard?limit=5&type=top`),
        apiClient.get(`/stats/leaderboard?limit=5&type=bottom`),
        apiClient.get(`/transactions?limit=10000`),
        apiClient.get(`/payouts/overview`)
      ]);

      setMarkets(marketsRes?.data || []);
      setBets(betsRes?.data?.bets || []);
      setBetStats(betsRes?.data?.stats || { total_profit: 0, total_loss: 0, total_pending: 0 });
      setLeaderboard(leaderboardRes?.data || []);
      setBottomLeaderboard(bottomLeaderboardRes?.data || []);
      setTransactions(transactionsRes?.data || []);
      
      setToPayCount(settlementsRes?.data?.to_pay?.length || 0);
      setToConfirmCount(settlementsRes?.data?.to_confirm?.length || 0);
      
      refreshUser();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingBets = Array.isArray(bets) ? bets.filter(b => b?.status === 'pending') : [];
  const wonBets = Array.isArray(bets) ? bets.filter(b => b?.status === 'won') : [];
  const lostBets = Array.isArray(bets) ? bets.filter(b => b?.status === 'lost') : [];
  
  const userTotalBets = user?.total_bets || 0;
  const userWonBets = user?.won_bets || 0;
  const winRate = userTotalBets > 0 ? ((userWonBets / userTotalBets) * 100).toFixed(0) : 0;
  const totalBetsCount = userTotalBets;

  const renderBetsList = (betsList, emptyMessage) => {
    if (betsList.length === 0) {
      return (
        <div className="text-center py-6">
          <p className="text-xs text-muted-foreground">{emptyMessage}</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
        {betsList.slice(0, 10).map(bet => (
          <Link 
            key={bet.id} 
            to={`/market/${bet.market_id}`}
            className="block p-3 rounded-xl bg-secondary/30 border border-border/5 hover:bg-secondary/50 transition-colors"
          >
            <p className="font-bold text-sm truncate mb-1">{bet.market_title}</p>
            <div className="flex items-center justify-between mb-1">
              <Badge className={cn("px-1.5 py-0 text-[10px] font-bold", (bet?.position || 'yes') === 'yes' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400')}>
                {(bet?.position || 'yes') === 'yes' ? t('yes') : t('no')}
              </Badge>
              <div className="flex flex-col items-end">
                {bet.status === 'won' ? (
                  <>
                    <span className="font-mono text-xs font-bold text-green-400">
                      +{Math.max(0, (bet.payout || 0) - (bet.amount || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {language === 'de' ? 'Einsatz:' : 'Stake:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </>
                ) : bet.status === 'lost' ? (
                  <>
                    <span className="font-mono text-xs font-bold text-red-400">
                      -{(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {language === 'de' ? 'Einsatz:' : 'Stake:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </>
                ) : bet.status === 'refunded' ? (
                  <>
                    <span className="font-mono text-xs font-bold text-muted-foreground">
                      0,00 €
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {language === 'de' ? 'Erstattet:' : 'Refunded:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-mono text-xs font-bold text-yellow-500">
                      {(bet?.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                    {bet.potential_payout > 0 && (
                      <span className="text-[10px] text-green-400/70 font-medium mt-0.5">
                        {language === 'de' ? 'Mögl. Gewinn: +' : 'Pot. Profit: +'}
                        {Math.max(0, (bet.potential_payout || 0) - (bet.amount || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {new Date(bet.created_at).toLocaleString(undefined, { 
                day: '2-digit', 
                month: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </p>
          </Link>
        ))}
        {betsList.length > 10 && (
          <Link to="/profile" className="block text-center text-xs text-muted-foreground hover:text-foreground pt-1">
            {t('viewAll')}
          </Link>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="app-container" data-testid="dashboard-loading">
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-24 sm:h-32 rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-8 w-48" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-64 rounded-xl" />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-96 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="dashboard-page">
      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        
        {/* Settlements Alert Banner */}
        {(toPayCount > 0 || toConfirmCount > 0) && (
          <div className="bg-gradient-to-r from-amber-500/[0.06] to-yellow-600/[0.02] border border-amber-500/15 shadow-[0_4px_20px_rgba(245,158,11,0.05)] rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 backdrop-blur-md hover:border-amber-500/20 transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 text-amber-500 rounded-xl shrink-0 shadow-[0_0_12px_rgba(245,158,11,0.08)]">
                <AlertCircle className="w-4.5 h-4.5 animate-pulse" />
              </div>
              <div className="space-y-0.5">
                <p className="font-bold text-xs sm:text-sm text-foreground tracking-tight">
                  {t('pendingSettlementsAlert')}
                </p>
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  {language === 'de'
                    ? `Du hast ${toPayCount} Zahlungen offen und ${toConfirmCount} Bestätigungen ausstehend.`
                    : `You have ${toPayCount} payments to make and ${toConfirmCount} receipts to confirm.`}
                </p>
              </div>
            </div>
            <Link to="/settlements" className="w-full sm:w-auto shrink-0">
              <Button size="sm" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-black font-bold gap-1 text-xs h-8 sm:h-8.5 shadow-md shadow-amber-500/10 hover:shadow-amber-500/25 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 rounded-xl px-4">
                <span>{language === 'de' ? 'Abrechnen' : 'Settle Now'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        )}

        {/* Welcome & Stats */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold">
              {t('welcomeBackUser')} <span className="gradient-text">{user?.username}</span>
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">{t('tradingOverview')}</p>
          </div>
          
          <Link to="/create-market" className="w-full md:w-auto">
            <Button className="w-full md:w-auto bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold shadow-lg shadow-red-500/20 hover:shadow-red-500/35 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 h-10 px-5 rounded-xl flex items-center justify-center gap-2" data-testid="create-market-btn">
              <Plus className="w-4 h-4" />
              {t('createMarket')}
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="stats-grid">
          <StatsCard
            title={t('netProfitLoss')}
            value={(user?.total_profit ?? betStats.total_profit) - (user?.total_loss ?? betStats.total_loss)}
            icon={Wallet}
          />
          <StatsCard
            title={t('won')}
            value={Math.max(0, user?.total_profit ?? betStats.total_profit)}
            icon={Trophy}
            className="text-green-500"
          />
          <StatsCard
            title={t('lost')}
            value={user?.total_loss ?? betStats.total_loss}
            icon={XCircle}
            className="text-red-500"
          />
          <StatsCard
            title={t('activeWager')}
            value={user?.total_pending ?? betStats.total_pending}
            icon={Target}
          />
          <StatsCard
            title={t('totalBets')}
            value={totalBetsCount}
            icon={TrendingUp}
          />
          <StatsCard
            title={t('winRate')}
            value={`${winRate}%`}
            icon={Trophy}
          />
        </div>

        {/* Balance Graph */}
        {Array.isArray(transactions) && transactions.length > 0 && (
          <div className="bg-card border border-border/50 rounded-xl p-3 sm:p-6">
             <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">{t('balanceOverTime')}</h3>
             <div className="h-[200px] sm:h-[300px] w-full">
               <BalanceGraph transactions={transactions} bets={bets} />
             </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Markets */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">{t('hotMarkets')}</h2>
              <Link to="/markets">
                <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
                  {t('viewAll')} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {markets.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {markets.slice(0, 6).map(market => (
                  <MarketCard key={market.id} market={market} />
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground mb-4">{t('noMarketsFound')}</p>
                <Link to="/create-market">
                  <Button className="bg-red-600 hover:bg-red-700">
                    {t('createMarket')}
                  </Button>
                </Link>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            {/* Your Bets */}
            <Card className="border-border/50">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  {t('yourBets')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <Tabs defaultValue="pending" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-4">
                    <TabsTrigger value="pending" className="text-xs">
                      {t('pending') || 'Offen'} ({pendingBets.length})
                    </TabsTrigger>
                    <TabsTrigger value="won" className="text-xs">
                      {t('won') || 'Gewonnen'} ({wonBets.length})
                    </TabsTrigger>
                    <TabsTrigger value="lost" className="text-xs">
                      {t('lost') || 'Verloren'} ({lostBets.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="pending" className="mt-0">
                    {renderBetsList(pendingBets, t('noOpenBets'))}
                  </TabsContent>
                  
                  <TabsContent value="won" className="mt-0">
                    {renderBetsList(wonBets, language === 'de' ? 'Keine gewonnenen Wetten.' : 'No won bets.')}
                  </TabsContent>
                  
                  <TabsContent value="lost" className="mt-0">
                    {renderBetsList(lostBets, language === 'de' ? 'Keine verlorenen Wetten.' : 'No lost bets.')}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="border-border/50">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  {t('leaderboard')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <Tabs defaultValue="top" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="top" className="text-xs">
                      {t('topTraders')}
                    </TabsTrigger>
                    <TabsTrigger value="bottom" className="text-xs">
                      {t('bottomTraders')}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="top" className="mt-0">
                    <div className="space-y-4">
                      {leaderboard.map((trader, i) => (
                        <div key={trader.username} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={cn("font-bold text-sm w-4", i === 0 ? "text-yellow-500" : i === 1 ? "text-gray-400" : i === 2 ? "text-amber-600" : "text-muted-foreground")}>
                              {i + 1}
                            </span>
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={trader.avatar_url} alt={trader.username} className="object-cover" />
                              <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-[10px]">
                                {trader.username?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-bold text-sm truncate max-w-[100px]">{trader?.username}</span>
                          </div>
                          <span className={cn("font-mono text-xs font-bold", (trader?.balance || 0) < 0 ? "text-red-400" : "text-green-400")}>
                            {(trader?.balance || 0) >= 0 ? '+' : ''}{(trader?.balance || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="bottom" className="mt-0">
                    <div className="space-y-4">
                      {bottomLeaderboard.map((trader, i) => (
                        <div key={trader.username} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={cn("font-bold text-sm w-4", i === 0 ? "text-red-500" : i === 1 ? "text-orange-500" : i === 2 ? "text-yellow-600" : "text-muted-foreground")}>
                              {i + 1}
                            </span>
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={trader.avatar_url} alt={trader.username} className="object-cover" />
                              <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-[10px]">
                                {trader.username?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-bold text-sm truncate max-w-[100px]">{trader?.username}</span>
                          </div>
                          <span className={cn("font-mono text-xs font-bold", (trader?.balance || 0) < 0 ? "text-red-400" : "text-green-400")}>
                            {(trader?.balance || 0) >= 0 ? '+' : ''}{(trader?.balance || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
